import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowRightLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { CurrencyInput } from "../../components/CurrencyInput";
import { formatISK, parseCurrencyInput } from "../../lib/currency";

const VEHICLE_TYPES = ["standard"]; // future-proof: admin adds more via DB later

export default function AdminTransferRoutePrices() {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | row
  const [saving, setSaving] = useState(false);

  const locById = useMemo(() => {
    const m = {};
    locations.forEach((l) => {
      m[l.id] = l;
    });
    return m;
  }, [locations]);

  const load = async () => {
    setLoading(true);
    try {
      const [rp, lc] = await Promise.all([
        api.get("/admin/transfer-route-prices"),
        api.get("/admin/transfer-locations"),
      ]);
      setRows(Array.isArray(rp.data) ? rp.data : []);
      setLocations(Array.isArray(lc.data) ? lc.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (r) => {
    if (!r.active && (!r.price_isk || r.price_isk <= 0)) {
      toast.error("Set a price greater than 0 kr before activating");
      return;
    }
    try {
      await api.put(`/admin/transfer-route-prices/${r.id}`, { active: !r.active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    const pname = locById[r.pickup_location_id]?.name || "(unknown)";
    const dname = locById[r.dropoff_location_id]?.name || "(unknown)";
    if (!window.confirm(`Delete route price "${pname} → ${dname}"?`)) return;
    try {
      await api.delete(`/admin/transfer-route-prices/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/admin/transfer-route-prices/${editing.id}`, form);
        toast.success("Route price updated");
      } else {
        await api.post("/admin/transfer-route-prices", form);
        toast.success("Route price created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-route-prices-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Transfer Route Prices</h1>
          <p className="text-sm text-slate-500">
            Fixed kr prices for standard pickup ↔ drop-off combinations. Bidirectional — entry A → B auto-matches B → A.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="rp-new-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={14} /> New route price
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Pickup</th>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Drop-off</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No route prices yet</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id} data-testid={`rp-row-${r.id}`}>
                    <td className="px-4 py-3">{locById[r.pickup_location_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-400">
                      <ArrowRightLeft size={14} />
                    </td>
                    <td className="px-4 py-3">{locById[r.dropoff_location_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{r.vehicle_type}</td>
                    <td className="px-4 py-3 tabular-nums">{formatISK(r.price_isk)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(r)}
                        data-testid={`rp-toggle-active-${r.id}`}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                          r.active
                            ? "bg-emerald-100 text-emerald-900 ring-emerald-200"
                            : "bg-slate-100 text-slate-600 ring-slate-200"
                        }`}
                      >
                        {r.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`rp-edit-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`rp-delete-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RoutePriceEditor
          initial={editing}
          locations={locations}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function RoutePriceEditor({ initial, locations, onCancel, onSave, saving }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    pickup_location_id: initial.pickup_location_id || "",
    dropoff_location_id: initial.dropoff_location_id || "",
    vehicle_type: initial.vehicle_type || "standard",
    price_isk: initial.price_isk ?? 0,
    active: initial.active !== undefined ? initial.active : true,
    notes: initial.notes || "",
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.pickup_location_id || !form.dropoff_location_id) {
      toast.error("Both pickup and drop-off are required");
      return;
    }
    if (form.pickup_location_id === form.dropoff_location_id) {
      toast.error("Pickup and drop-off cannot be the same");
      return;
    }
    const priceNum = parseCurrencyInput(form.price_isk);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast.error("Price must be a non-negative number");
      return;
    }
    if (form.active && priceNum <= 0) {
      toast.error("Set a price greater than 0 kr before activating");
      return;
    }
    onSave({
      pickup_location_id: form.pickup_location_id,
      dropoff_location_id: form.dropoff_location_id,
      vehicle_type: form.vehicle_type || "standard",
      price_isk: priceNum,
      active: !!form.active,
      notes: form.notes || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-6"
      onClick={onCancel}
      data-testid="rp-editor-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-semibold">{isEdit ? "Edit route price" : "New route price"}</h3>
          <button type="button" onClick={onCancel} aria-label="close" className="text-slate-500">
            <X size={18} />
          </button>
        </div>

        <Labeled label="Pickup location *">
          <select
            data-testid="rp-field-pickup"
            required
            value={form.pickup_location_id}
            onChange={(e) => setForm({ ...form, pickup_location_id: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          >
            <option value="" disabled>Select pickup…</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{!l.active ? " (disabled)" : ""}
              </option>
            ))}
          </select>
        </Labeled>

        <Labeled label="Drop-off location *">
          <select
            data-testid="rp-field-dropoff"
            required
            value={form.dropoff_location_id}
            onChange={(e) => setForm({ ...form, dropoff_location_id: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          >
            <option value="" disabled>Select drop-off…</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{!l.active ? " (disabled)" : ""}
              </option>
            ))}
          </select>
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labeled label="Vehicle type">
            <select
              data-testid="rp-field-vehicle"
              value={form.vehicle_type}
              onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
            >
              {VEHICLE_TYPES.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Labeled>
          <Labeled label="Price (kr) *">
            <CurrencyInput
              data-testid="rp-field-price"
              required
              value={form.price_isk}
              onChange={(value) => setForm({ ...form, price_isk: value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums outline-none focus:border-slate-900"
            />
          </Labeled>
        </div>

        <Toggle
          label="Active (available for instant pricing on the booking form)"
          testid="rp-field-active"
          checked={form.active}
          onChange={(v) => setForm({ ...form, active: v })}
        />

        <Labeled label="Internal notes">
          <textarea
            data-testid="rp-field-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          />
        </Labeled>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="rp-editor-save"
            className="flex-1 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create route price"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
      />
      {label}
    </label>
  );
}
